import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const PATH = "/heritage/livsberattelser";

const USE_CASES = [
  "Intervju med förälder eller morförälder",
  "Släkthistoria",
  "Personligt porträtt",
  "Minnesfilm",
  "Filmad hälsning till familjen",
  "Dokumentation inför framtiden",
];

const PROCESS = [
  { label: "Första samtal", body: "Vi förstår syftet, vem som ska intervjuas och hur filmen ska användas i familjen." },
  { label: "Planering och frågeunderlag", body: "Vi tar fram områden och frågor tillsammans. Personen får se frågorna i förväg om så önskas." },
  { label: "Filmad intervju", body: "Inspelning i en lugn, trygg miljö — hemma eller på en plats med personlig betydelse." },
  { label: "Klippning och bearbetning", body: "Materialet klipps med ljud-, färg- och strukturarbete. Foton eller arkiv kan vävas in." },
  { label: "Leverans", body: "Filmen levereras digitalt direkt till familjen, redo att delas privat." },
];

const PACKAGES = [
  {
    name: "Livsintervjun",
    body:
      "Mindre upplägg för en filmad intervju med en person. Innehåller förmöte, frågeunderlag, filmad intervju, klippning och digital leverans.",
  },
  {
    name: "Familjearvet",
    body:
      "Mer omfattande upplägg med längre intervju, kapitelindelning och möjlighet att inkludera bilder eller arkivmaterial. Förintervju med familjen, utvecklat frågeunderlag, huvudfilm och kortare utdrag.",
  },
  {
    name: "Legacy",
    body:
      "För familjer som vill skapa ett mer omfattande filmat arv med flera intervjuer, tydlig berättelsestruktur, arkivmaterial, huvudfilm, kortare klipp och privat leverans.",
  },
];

const FAQ = [
  {
    q: "Vad är en filmad livsberättelse?",
    a: "En filmad livsberättelse är en strukturerad intervju där en person berättar om sitt liv, sina minnen, familj, arbete och erfarenheter. Resultatet är en eller flera filmer som familjen kan bevara och dela vidare.",
  },
  {
    q: "Vem brukar beställa en livsberättelse?",
    a: "Oftast barn eller barnbarn som vill bevara en förälders eller mor- och farförälders berättelse. Det förekommer också att personen själv tar initiativet.",
  },
  {
    q: "Var spelas intervjun in?",
    a: "I en miljö som känns trygg för personen som intervjuas — hemma, hos en släkting eller på en plats med personlig betydelse. Vi tar med all utrustning.",
  },
  {
    q: "Hur lång blir filmen?",
    a: "En huvudfilm är oftast 20–45 minuter, ibland uppdelad i kapitel. Vi kan också göra kortare utdrag som är lättare att titta på i vardagen.",
  },
  {
    q: "Kan vi använda gamla foton eller filmer?",
    a: "Ja. I Familjearvet och Legacy kan vi väva in fotografier, dokument eller äldre filmmaterial som kompletterar berättelsen.",
  },
  {
    q: "Förblir materialet privat?",
    a: "Ja. Filmen levereras direkt till familjen och delas inte vidare. Ni bestämmer själva vem som får se den.",
  },
];

export const Route = createFileRoute("/heritage/livsberattelser")({
  head: () => ({
    meta: [
      { title: "Livsberättelser på film — Fenny Heritage" },
      {
        name: "description",
        content:
          "Filmade livsberättelser för familjer. Vi hjälper er att dokumentera en närståendes liv, minnen och berättelse i professionell form.",
      },
      { property: "og:title", content: "Livsberättelser på film — Fenny Heritage" },
      {
        property: "og:description",
        content:
          "Bevara en närståendes berättelse genom en professionellt filmad intervju. Tryggt upplägg, hög kvalitet, privat leverans.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
    ],
    links: [
      { rel: "canonical", href: `https://fenny.se${PATH}` },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/heritage/livsberattelser", name: "Livsberättelser på film — Fenny Heritage", description: "Filmade livsberättelser för familjer. Vi hjälper er att dokumentera en närståendes liv, minnen och berättelse i professionell form."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Livsberättelser på film",
            path: PATH,
            description:
              "Fenny Heritage producerar filmade livsberättelser för familjer — strukturerade intervjuer som dokumenterar en persons liv, minnen och berättelse.",
            serviceType: "Filmad livsberättelse",
            priceRange: "Offert efter första samtal",
            areaServed: ["Sweden"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Heritage", path: "/heritage" },
            { name: "Livsberättelser", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: LivsberattelserPage,
});

function LivsberattelserPage() {
  return (
    <article className="pt-28">
      <Section className="pb-10">
        <div className="max-w-4xl">
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">
            Heritage Personal
          </div>
          <h1 className="text-balance text-5xl font-semibold leading-[1.02] md:text-7xl">
            Filmade livsberättelser
          </h1>
          <p className="lede mt-6 max-w-3xl text-pretty text-lg text-foreground/85 md:text-xl" data-speakable>
            En filmad intervju där en person får berätta om sitt liv, sin uppväxt,
            familj, arbete, erfarenheter och viktiga minnen — bevarad för barn,
            barnbarn och kommande generationer.
          </p>
          <QuickAnswer
            className="mt-6"
            text="En filmad livsberättelse är en strukturerad intervju där en person berättar om sitt liv, sina minnen och erfarenheter. Filmen bevaras digitalt för familjen och kan delas privat."
          />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-3 text-sm font-medium text-background hover:opacity-90"
            >
              Boka ett första samtal <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/heritage"
              className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-3 text-sm font-medium hover:bg-foreground/[0.05]"
            >
              Tillbaka till Heritage
            </Link>
          </div>
        </div>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Exempel på användning</h2>
        <ul className="mt-8 grid gap-3 md:grid-cols-2">
          {USE_CASES.map((u) => (
            <li key={u} className="rounded-xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 text-foreground/85">
              {u}
            </li>
          ))}
        </ul>
      </Section>

      <Section className="py-10" id="process">
        <h2 className="text-3xl font-semibold md:text-4xl">Så går det till</h2>
        <ol className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {PROCESS.map((s, i) => (
            <li key={s.label} className="rounded-2xl border border-white/10 bg-surface/40 p-5">
              <div className="text-xs uppercase tracking-widest text-mint">{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-2 font-medium">{s.label}</div>
              <p className="mt-2 text-sm text-foreground/75">{s.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section className="py-10" id="paket">
        <h2 className="text-3xl font-semibold md:text-4xl">Paket</h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {PACKAGES.map((p) => (
            <div key={p.name} className="rounded-2xl border border-white/10 bg-surface/40 p-6">
              <div className="font-medium">{p.name}</div>
              <p className="mt-3 text-sm text-foreground/75">{p.body}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-foreground/65">
          Priser lämnas som offert efter första samtalet.
        </p>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">Vanliga frågor</h2>
        <div className="mt-8 max-w-3xl space-y-6">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="text-lg font-medium">{f.q}</h3>
              <p className="mt-2 text-foreground/75">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <RelatedLinksRail sourcePath={PATH} />
      <SignatureCta />
    </article>
  );
}
